import React, { useCallback, useEffect, useState } from 'react';
import { Pack, Repack } from 'types/generated/graphql';
import {
    CtaGoal,
    CtaType,
    InteractionSource,
} from 'utils/analytics/AnalyticsV2Service.generated';
import { getPackPlayButtonClickTrackerV2 } from 'utils/analytics/helpers/trackAssetPlayed';
import { Badge, Box, Flex, useBreakpoint, useHover } from '@landr/maestro';
import {
    PlayerProvider,
    usePlayerContext,
} from 'contexts/PlayerContext/PlayerContext';
import { getStreamEntityType } from 'helpers';
import { DownloadButton } from 'components/CommonUi';
import { FavoriteButton } from 'components/FavoriteButton';
import { t } from '@lingui/macro';
import { css } from '@landr/styled-system/css';
import PlayerButton from 'components/CommonUi/PlayerButton/PlayerButton';
import { analytics } from 'utils';
import { usePreviewContext } from '../contexts/PreviewContext/PreviewContext';
import WaveformIcon from './WaveformIcon';

export const useCardPlayer = ({ item }: { item: Pack | Repack }) => {
    const { contentGroup, contentList } = usePreviewContext();

    const onPlayButtonClick = useCallback(
        (totalTime: number) => {
            getPackPlayButtonClickTrackerV2({
                interactionSource: InteractionSource.CardAction,
                item: item as Pack, // https://mixgenius.atlassian.net/browse/SP-8405
                contentGroup,
                contentList,
            })(totalTime);
        },
        [item, contentGroup, contentList],
    );

    return { onPlayButtonClick };
};

const progressBarClass = css({
    height: '100%',
    position: 'relative',
    backgroundColor: 'surface.inverted',
    transition: 'width 0.1s ease-out',
    zIndex: 10,
});

const progressBarWrapperClass = css({
    transition: 'transform 0.3s ease-in-out, height 0.2s ease-in-out',
    '&:hover': {
        height: '16px',
    },
});

const PlayerProgressBar = () => {
    const { currentTime, totalTime, seek } = usePlayerContext();
    const progress = totalTime > 0 ? (currentTime / totalTime) * 100 : 0;

    const handleProgressClick = (event: React.MouseEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();

        const rect = event.currentTarget.getBoundingClientRect();
        const clickX = event.clientX - rect.left;
        const clickPercentage = (clickX / rect.width) * 100;
        const seekTime = (clickPercentage / 100) * totalTime;

        seek(seekTime, true);
    };

    return (
        <Flex
            width="100%"
            height="100%"
            position="relative"
            cursor="pointer"
            onClick={handleProgressClick}
        >
            <Flex
                className={progressBarClass}
                style={{ width: `${progress}%` }}
            />
            <Flex
                width="100%"
                height="100%"
                position="absolute"
                bottom="0"
                backgroundColor="surface.neutral.medium"
            />
        </Flex>
    );
};

export const PlayerActionsContainer = ({
    children,
    isHovered,
}: {
    children: React.ReactNode;
    isHovered?: boolean;
}) => {
    const { playing } = usePlayerContext();
    const isMobile = useBreakpoint('md');
    const isVisible = isMobile || isHovered || playing;

    return (
        <Flex
            className={`${actionsContainerStyles} ${
                isVisible ? 'visible' : ''
            } ${isMobile ? 'mobile' : ''}`}
        >
            {children}
        </Flex>
    );
};

export const CardPlayer = ({ item }: { item: Pack | Repack }) => {
    const { onPlayButtonClick } = useCardPlayer({
        item,
    });
    const { playing, loading } = usePlayerContext();
    const [hoverRef, isHovered] = useHover(false);
    const isMobile = useBreakpoint('md');
    const [showWaveformIcon, setWaveformIcon] = useState(false);

    useEffect(() => {
        setWaveformIcon(playing && !loading && !isHovered && !isMobile);
    }, [isHovered, loading, playing, isMobile]);

    return (
        <>
            <Flex ref={hoverRef}>
                <Flex>
                    <PlayerButton
                        id={item.guid}
                        callback={onPlayButtonClick}
                        size="lg"
                        variant="secondary"
                        isRounded
                        isDemo
                        key="player-button"
                        isPackPreview={true}
                    />
                </Flex>
                <Flex
                    position="absolute"
                    opacity={showWaveformIcon ? 1 : 0}
                    zIndex={showWaveformIcon ? 2 : -1}
                    transition="opacity 0.3s ease-in-out 0.1s"
                >
                    <WaveformIcon
                        barHeight={30}
                        barWidth={2}
                        barCount={6}
                        durationInSeconds={0.25}
                        barColor="surface.low"
                        verticalAlignment="center"
                        cornerRadius={4}
                    />
                </Flex>
            </Flex>
            <Flex
                width="100%"
                height="8px"
                position="absolute"
                bottom="0"
                left="0"
                opacity={1}
                className={progressBarWrapperClass}
                style={{
                    transform: playing ? 'translateY(0)' : 'translateY(100%)',
                }}
            >
                <PlayerProgressBar />
            </Flex>
        </>
    );
};

const actionsContainerStyles = css({
    gap: 'xs',
    alignItems: 'center',
    justifyContent: 'center',
    w: '100%',
    h: '100%',
    opacity: 0,
    position: 'relative',
    zIndex: 10,
    '&.visible': {
        opacity: 1,
    },
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'surface.inverted/20',
        opacity: 0,
        transition: 'opacity 0.3s ease-in-out',
        pointerEvents: 'none',
        zIndex: -1,
    },
    '&.visible::before': {
        opacity: 1,
    },
    '&.mobile::before': {
        display: 'none',
    },
    '&.mobile.visible::before': {
        display: 'none',
    },
});

const PreviewActions = ({
    item,
    isHovered,
    isTakenDownUIVisible,
}: {
    item: Pack | Repack;
    isHovered?: boolean;
    isTakenDownUIVisible?: boolean;
}) => {
    const isMobile = useBreakpoint('md');
    const { contentGroup, contentList } = usePreviewContext();

    const trackCTADownloadClicked = () => {
        analytics.trackCTAClicked({
            ctaGoal: CtaGoal.DownloadSample,
            ctaType: CtaType.Button,
            interactionSource: InteractionSource.AssetAction,
            ctaText: item.name,
            ctaContentGroup: contentGroup,
            ctaContentList: contentList,
        });
    };

    const trackCTAFavoriteClicked = () => {
        analytics.trackCTAClicked({
            ctaGoal: CtaGoal.FavouriteSample,
            ctaType: CtaType.Button,
            interactionSource: InteractionSource.AssetAction,
            ctaText: item.name,
            ctaContentGroup: contentGroup,
            ctaContentList: contentList,
        });
    };

    return (
        <PlayerProvider
            playerId={item.guid}
            streamEntityType={getStreamEntityType(item.type)}
        >
            <PlayerActionsContainer isHovered={isHovered}>
                {!isMobile && !isTakenDownUIVisible && (
                    <FavoriteButton
                        key={`reconciliation-isFavorited-${item.isFavorited}`}
                        size="sm"
                        variant="subtle"
                        isRounded
                        data-testid="favorite-pack-button"
                        item={item}
                        onClick={trackCTAFavoriteClicked}
                    />
                )}

                <CardPlayer item={item} />

                {!isMobile && (
                    <DownloadButton
                        item={item}
                        size="sm"
                        variant="subtle"
                        isRounded
                        key="download-button"
                        data-testid="download-pack-button"
                        label={item?.isDownloaded ? t`Download` : t`Get pack`}
                        onClick={trackCTADownloadClicked}
                    />
                )}
                {isTakenDownUIVisible && (
                    <Box position="absolute" top="0" left="xs">
                        <Badge
                            variant="warning"
                            kind="subtle"
                            label={t`unavailable`}
                            size="sm"
                            isRounded
                        />
                    </Box>
                )}
            </PlayerActionsContainer>
        </PlayerProvider>
    );
};

export default PreviewActions;
